import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ExternalLink, MapPin, Clock, Phone, Upload, Receipt, ShieldCheck, Car, Gavel, FileCheck } from "lucide-react";

function InfoBlock({ icon: Icon, title, children }) {
  return (
    <div className="border border-border bg-card rounded-sm p-5">
      <div className="flex items-center gap-2 text-sm font-semibold mb-2"><Icon className="h-4 w-4 text-primary" /> {title}</div>
      <div className="text-sm text-muted-foreground whitespace-pre-line leading-relaxed">{children}</div>
    </div>
  );
}

export default function CourtDetail() {
  const { id } = useParams();
  const [c, setC] = useState(null);
  useEffect(() => { api.get(`/courts/${id}`).then((r) => setC(r.data)).catch(() => {}); }, [id]);

  if (!c) return <div className="py-20 text-center text-muted-foreground">Loading…</div>;

  return (
    <div data-testid="court-detail-page">
      <Link to="/courts" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary mb-4"><ArrowLeft className="h-4 w-4" /> Court Center</Link>

      <div className="relative rounded-sm overflow-hidden mb-6 animate-fade-up">
        <img src={c.image_url} alt={c.name} className="w-full h-52 object-cover pointer-events-none" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#051E36]/90 to-[#051E36]/20" />
        <div className="absolute bottom-0 p-6">
          <div className="text-[11px] uppercase tracking-[0.2em] text-primary mb-1">{c.jurisdiction}</div>
          <h1 className="font-display font-extrabold text-3xl text-white">{c.name}</h1>
          {c.website && (
            <a href={c.website} target="_blank" rel="noreferrer" data-testid="court-detail-website">
              <Button size="sm" className="mt-3"><ExternalLink className="h-4 w-4 mr-1.5" /> Official Court Website</Button>
            </a>
          )}
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4 mb-4">
        <InfoBlock icon={MapPin} title="Address">{c.address}</InfoBlock>
        <InfoBlock icon={Clock} title="Hours">{c.hours}</InfoBlock>
        <InfoBlock icon={Phone} title="Clerk Contact">{c.clerk_contact}</InfoBlock>
      </div>

      <div className="grid md:grid-cols-2 gap-4 mb-4">
        <InfoBlock icon={Upload} title="E-Filing">
          <div className="whitespace-pre-line">{`${c.efiling}\n\n${c.efiling_notes || ""}`}</div>
          {c.efiling_url && (
            <a href={c.efiling_url} target="_blank" rel="noreferrer" data-testid="court-efiling-link">
              <Button size="sm" variant="outline" className="mt-3"><ExternalLink className="h-3.5 w-3.5 mr-1.5" /> Open E-Filing Portal</Button>
            </a>
          )}
        </InfoBlock>
        <InfoBlock icon={Receipt} title="Filing Fee Schedule">{c.filing_fees}</InfoBlock>
        <InfoBlock icon={FileCheck} title="Service Requirements">{c.service_rules}</InfoBlock>
        <InfoBlock icon={Car} title="Parking & Logistics">{c.parking}</InfoBlock>
        <InfoBlock icon={ShieldCheck} title="Security & Access">{c.security}</InfoBlock>
      </div>

      {c.judges?.length > 0 && (
        <div className="border border-border bg-card rounded-sm p-5">
          <div className="flex items-center gap-2 text-sm font-semibold mb-3"><Gavel className="h-4 w-4 text-primary" /> Judges & Standing Orders</div>
          <Accordion type="single" collapsible className="w-full" data-testid="judges-accordion">
            {c.judges.map((j) => (
              <AccordionItem key={`${j.name}-${j.division}`} value={`${j.name}-${j.division}`}>
                <AccordionTrigger className="text-left">
                  <span><span className="font-semibold">{j.name}</span> <span className="text-xs text-muted-foreground ml-2">{j.division}</span></span>
                </AccordionTrigger>
                <AccordionContent className="text-sm text-muted-foreground leading-relaxed">{j.standing_order}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      )}
    </div>
  );
}
